import React, { useState } from 'react';
import { Dialog } from '../lib/dialog';
import { Button } from '../lib/button';
import { ExclusiveButtons } from '../lib/exclusive-buttons';
import { FormField } from '../lib/forms';
import { exportAndDownload, ExportType, ExportFilters } from '../service/importExportService';
import { getErrorMessage } from '../utils/errorUtils';

interface ExportOption {
  value: ExportType;
  label: string;
  description: string;
}

const EXPORT_OPTIONS: ExportOption[] = [
  {
    value: 'full',
    label: 'Download',
    description: 'Export data searched in previous screen (full list of current selection) with all IDs.',
  },
  {
    value: 'update',
    label: 'Update',
    description: 'Export data with internal codes for updating / changing data in bulk offline.',
  },
  {
    value: 'create',
    label: 'Create',
    description: 'Download a blank template for adding new records in bulk.',
  },
];

export interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Opcjonalne filtry z aktualnego stanu wyszukiwania na MasterDataPage */
  filters?: ExportFilters;
}

export const ExportDialog: React.FC<ExportDialogProps> = ({ open, onClose, filters = {} }) => {
  const [selected, setSelected] = useState<ExportType>('full');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleExport = async () => {
    setLoading(true);
    setError(undefined);
    try {
      await exportAndDownload(selected, filters);
      // Reset type selection only after a successful export so repeated attempts
      // don’t force the user to re-select the same option.
      setSelected('full');
      onClose();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setError(undefined);
    onClose();
  };

  const activeDescription = EXPORT_OPTIONS.find((o) => o.value === selected)?.description;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Export data"
      footer={
        <Button variant="primary" onClick={handleExport} processing={loading}>
          Export
        </Button>
      }
      footerExtras={<Button onClick={handleClose} disabled={loading}>Cancel</Button>}
    >
      <FormField label="Export type" required>
        <ExclusiveButtons<ExportType>
          items={EXPORT_OPTIONS.map((o) => o.value)}
          value={selected}
          onChange={setSelected}
          textFn={(v) => EXPORT_OPTIONS.find((o) => o.value === v)!.label}
          disabled={loading}
        />
      </FormField>

      {activeDescription && (
        <p className="help" style={{ marginTop: 4 }}>{activeDescription}</p>
      )}

      {error && (
        <p className="help is-danger" style={{ marginTop: 12 }}>{error}</p>
      )}
    </Dialog>
  );
};
